/* eslint-disable no-restricted-imports */
/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { productDetailsPriceSX } from '@/components/blocks/ProductDetails/styles/priceStrikethru';
import { InfoElectronico } from '@/components/content/ProductDetails';
import { calculateProductPriceIncrease } from '@/data/Content/BasicCheckOut/utils/calculateProductPriceIncrease';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { ProductType } from '@/data/types/Product';
import { formatPrice } from '@/utils/formatPrice';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { isKitOrBundleType } from '@/utils/productIsA';
import { Box, Stack, styled, Typography } from '@mui/material';
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';
import { FC, useContext, useMemo } from 'react';

const Img = styled('img')({
	height: '24px',
	marginLeft: '4px',
});

export const useProductDetailsPrice = (product?: ProductType, sku?: ProductType) => {
	const { offer, list, currency } = useMemo<{
		offer: number;
		list: number;
		currency: string;
		min: number;
	}>(() => {
		const { prices } = getProductDisplayInfo(sku, product);
		const defaults = { offer: 0, list: 0, currency: '' };
		return { ...defaults, ...prices, min: sku?.productPrice?.min || 0 };
	}, [sku, product]);

	const { price, priceWithoutOffer, additionalOffer } = calculateProductPrices({
		list,
		offer,
		ribbons: sku?.ribbons ?? [],
	});

	return { price, priceWithoutOffer, additionalOffer, offer, list, currency };
};

export const ProductDetailsPrice: FC<{
	infoElectronico?: InfoElectronico;
	orderId?: string;
}> = ({ infoElectronico }) => {
	const { selection, product } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	> &
		ReturnType<typeof useSkuListTable>;

	const isKitOrBundle = isKitOrBundleType[product?.type ?? 'unknown'];
	const sku = !isKitOrBundle ? selection?.sku : product;
	const { offer, list } = useMemo<{
		offer: number;
		list: number;
		currency: string;
		min: number;
	}>(() => {
		const { prices } = getProductDisplayInfo(sku, product);
		const defaults = { offer: 0, list: 0, currency: '' };
		return { ...defaults, ...prices, min: sku?.productPrice?.min || 0 };
	}, [sku, product]);

	const { price, priceWithoutOffer } = calculateProductPrices({
		list,
		offer,
		ribbons: sku?.ribbons ?? [],
	});
	const codes = product?.keyword?.split(',') ?? ['', ''];
	return (
		<Stack direction="column" spacing={2} sx={{ marginTop: '9px' }}>
			<Typography
				component="span"
				data-testid="offer-price"
				id="offer-price"
				sx={productDetailsPriceSX}
			>
				{infoElectronico
					? 'Revisa el precio con tu distribuidor'
					: formatPrice('es-MX', 'MXN', calculateProductPriceIncrease(priceWithoutOffer))}
			</Typography>

			<Box>
				<Typography
					component={'h6'}
					sx={{ fontSize: 'var(--font-size-caption)', color: '#929292' }}
				>
					{'Modelo: '} {codes[0]}
				</Typography>

				<Typography
					component={'h6'}
					sx={{ fontSize: 'var(--font-size-caption)', color: '#929292' }}
				>
					{'Código: '} {codes[1]}
				</Typography>
			</Box>
		</Stack>
	);
};
